<template>
  <div>
    <div v-if="score && !hasEssentials">
      <GridHighlight
        type="stability"
        :text="score"
        :minScore="minScore"
        :maxScore="maxScore"
        :scoreDelta="scoreDelta"
        :large-text="true"
        class="ml-3"
        :show-title="true"
      ></GridHighlight>
    </div>
    <Loader v-if="isLoading" text="" color="white"></Loader>
    <div class="mt-2" v-if="riskRatings">
      <div class="text-left ml-3 mb-2 mr-2 pull-left">Risk Ratings</div>
      <PulseAndRiskDefinitions
        class="pull-left ratings-icon-container"
        :initial="'RR'"
      ></PulseAndRiskDefinitions>
      <div class="clearfix"></div>

      <RiskRatingPrimary
        :horizontal="true"
        :risks="riskRatings"
        :use-click="false"
        :dark="true"
        class="mb-5"
      ></RiskRatingPrimary>
    </div>
  </div>
</template>

<script>
import { getRiskRatingsByCountryCode } from "@/services/controlRisks/risks.service";
import { getStabilityByCountry } from "@/services/grids/stability.service";
import {
  hasEssentialsAccess,
  hasPlusCyberAccess,
} from "@/services/user/user.service";
import { RISK_RATING_PRIMARIES } from "@/common/constants/controlRisks/risks";

export default {
  name: "AssetsPaneRatings",
  components: {
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
    GridHighlight: () => import("@/components/grids/GridHighlight"),
    RiskRatingPrimary: () =>
      import("@/components/controlRisks/risks/RiskRatingPrimary"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    countryCode: {
      type: String,
    },
  },
  data() {
    // get primary risk ratings based on user access
    const primaryRiskRatings = Object.keys(RISK_RATING_PRIMARIES)
      .filter((key) => {
        if (!hasPlusCyberAccess() && key === "cyber") return false;
        return true;
      })
      .map((key) => ({ key, value: RISK_RATING_PRIMARIES[key] }));

    return {
      primaryRiskRatings: primaryRiskRatings,
      hasEssentials: hasEssentialsAccess(),
      stability: {},
      riskRatingCountries: [],
      score: 0,
      minScore: 0,
      maxScore: 0,
      scoreDelta: 0,
      isLoading: true,
    };
  },
  async mounted() {
    await this.getRatings();
  },
  computed: {
    riskRatingsCountry() {
      return this.riskRatingCountries?.countries?.[0] ?? [];
    },
    riskRatings() {
      if (this.riskRatingsCountry.length === 0) return null;

      return this.primaryRiskRatings
        .map(({ value }) => {
          const rating = this.riskRatingsCountry.properties.ratings.find(
            (r) => r.riskCategoryId === value
          );
          if (rating) {
            return {
              id: rating.riskCategoryId,
              type: rating.riskCategoryName,
              value: rating.rateValue,
            };
          }
        })
        .filter(Boolean);
    },
  },
  methods: {
    async getRatings() {
      await Promise.all([this.getRiskRatings(), this.getStability()]);
      this.isLoading = false;
    },
    async getStability() {
      this.stability = {};
      if (this.countryCode) {
        this.stability = await getStabilityByCountry({
          numDays: 1,
          aoi: this.countryCode,
          syncTileScores: true,
          local: true,
        });
      }
      // extract score, min and max
      const scoreCount = this.stability?.Item?.count;
      const min = this.stability?.Item?.stats?.percentiles?.very_low;
      const max = this.stability?.Item?.stats?.percentiles?.very_high;

      this.score = typeof scoreCount === "number" ? Math.floor(scoreCount) : 0;
      this.minScore = typeof min === "number" ? Math.floor(min) : 0;
      this.maxScore = typeof max === "number" ? Math.floor(max) : 0;
      this.scoreDelta = Math.floor(this.stability?.Item?.delta?.["1"]) ?? 0;
    },
    async getRiskRatings() {
      if (this.countryCode) {
        this.riskRatingCountries = await getRiskRatingsByCountryCode(
          this.countryCode
        );
      }
    },
  },
  watch: {
    countryCode: {
      handler: "getRatings",
      immediate: true,
      deep: true,
    },
  },
};
</script>
